'use client'

import React, { useRef, useState, useLayoutEffect, useCallback } from "react";
import Image from 'next/image';
// Use the native ResizeObserver to avoid legacy JS polyfills
import { LazyMotion, domAnimation, m, useScroll, useTransform, useSpring } from "framer-motion";

type ListSet = {
  title: string;
  imageSrc: string;
}

type ContentItem = {
  list1: ListSet[],
  list2: ListSet[],
  list3: ListSet[]
  list4: ListSet[]
  list5: ListSet[]
}

type ListVerticalScrollProps = {
  content: ContentItem[];
}

const ListVerticalScroll = ({ content }: ListVerticalScrollProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const ghostRef = useRef<HTMLDivElement | null>(null);
  const [scrollRange, setScrollRange] = useState(0);
  const [viewportW, setViewportW] = useState(0);

  useLayoutEffect(() => {
    if (scrollRef.current) {
      setScrollRange(scrollRef.current.scrollWidth);
    }
  }, [scrollRef]);

  const onResize = useCallback((entries: ResizeObserverEntry[]) => {
    for (let entry of entries) {
      setViewportW(entry.contentRect.width);
    }
  }, []);

  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => onResize(entries));
    const currentGhostRef = ghostRef.current;
    if (currentGhostRef) {
      resizeObserver.observe(currentGhostRef);
    }
    return () => {
      if (currentGhostRef) {
        resizeObserver.unobserve(currentGhostRef);
      }
    };
  }, [onResize]);

  const { scrollYProgress } = useScroll();

  const getTransformValues = (screenSize: string) => {
    switch (screenSize) {
      case "sm":
        return [-scrollRange + viewportW, (-scrollRange + -500) + viewportW];
      case "md":
        return [-scrollRange + viewportW, (-scrollRange + -1000) + viewportW];
      case "lg":
        return [-scrollRange + viewportW, (-scrollRange + -2000) + viewportW];
      default:
        return [-scrollRange + viewportW, (-scrollRange + -1500) + viewportW];
    }
  };

  const screenSize = (() => {
    if (viewportW < 768) return "sm";
    if (viewportW < 1024) return "md";
    return "lg";
  })();

  const [start, end] = getTransformValues(screenSize);

  const transform1 = useTransform(scrollYProgress, [0, 1], [0, end / 2]);
  const transform2 = useTransform(scrollYProgress, [1, 0], [0, start / 3]);
  const transform3 = useTransform(scrollYProgress, [0, 1], [0, end / 2]);
  const transform4 = useTransform(scrollYProgress, [1, 0], [0, start / 3]);
  const transform5 = useTransform(scrollYProgress, [0, 1], [0, end / 2]);

  const physics = { damping: 30, mass: 0.20, stiffness: 2000 };
  const spring1 = useSpring(transform1, physics);
  const spring2 = useSpring(transform2, physics);
  const spring3 = useSpring(transform3, physics);
  const spring4 = useSpring(transform4, physics);
  const spring5 = useSpring(transform5, physics);

  return (
    <React.Fragment>
      <LazyMotion features={domAnimation}>
        <div className="flex flex-col gap-3 md:gap-2">
          <div className="scroll-container">
            <m.section
              ref={scrollRef}
              style={{ x: spring1 }}
              className="thumbnails-container"
            >
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-4">
                {content.map((item, index) => (
                  <span key={index}>
                    {item.list1.map((badge, index) => {
                      const colorClasses = [
                        "bg-purple-200/10 text-purple-200 ring-1 ring-inset ring-purple-200",
                        "bg-blue-300/10 text-blue-300 ring-1 ring-inset ring-blue-300",
                        "bg-purple-300/10 text-purple-300 ring-1 ring-inset ring-purple-300",
                        "bg-purple-600/10 text-purple-600 ring-1 ring-inset ring-purple-600",
                        "bg-purple-400/10 text-purple-400 ring-1 ring-inset ring-purple-400",
                        "bg-purple-500/10 text-purple-500 ring-1 ring-inset ring-purple-500",
                      ];

                      const color = colorClasses[index % colorClasses.length]; // cycle colors

                      return (
                        <span
                          key={index}
                          className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-black me-2 mb-2 ${color} uppercase`}
                        >
                          {badge.title}
                        </span>
                      );
                    })}
                  </span>
                ))}
              </div>
            </m.section>
          </div>
          <div className="scroll-container">
            <m.section
              ref={scrollRef}
              style={{ x: spring2 }}
              className="thumbnails-container"
            >
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-4">
                {content.map((item, index) => (
                  <span key={index}>
                    {item.list2.map((badge, index) => {
                      const colorClasses = [
                        "bg-blue-300/10 text-blue-300 ring-1 ring-inset ring-blue-300",
                        "bg-purple-600/10 text-purple-600 ring-1 ring-inset ring-purple-600",
                        "bg-purple-500/10 text-purple-500 ring-1 ring-inset ring-purple-500",
                        "bg-purple-200/10 text-purple-200 ring-1 ring-inset ring-purple-200",
                        "bg-purple-300/10 text-purple-300 ring-1 ring-inset ring-purple-300",
                        "bg-purple-400/10 text-purple-400 ring-1 ring-inset ring-purple-400",
                      ];

                      const color = colorClasses[index % colorClasses.length]; // cycle colors

                      return (
                        <span
                          key={index}
                          className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-black me-2 mb-2 ${color} uppercase`}
                        >
                          {badge.title}
                        </span>
                      );
                    })}
                  </span>
                ))}
              </div>
            </m.section>
          </div>
          <div className="scroll-container">
            <m.section
              ref={scrollRef}
              style={{ x: spring3 }}
              className="thumbnails-container"
            >
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-4">
                {content.map((item, index) => (
                  <span key={index}>
                    {item.list3.map((badge, index) => {
                      const colorClasses = [
                        "bg-purple-200/10 text-purple-200 ring-1 ring-inset ring-purple-200",
                        "bg-blue-300/10 text-blue-300 ring-1 ring-inset ring-blue-300",
                        "bg-purple-300/10 text-purple-300 ring-1 ring-inset ring-purple-300",
                        "bg-purple-600/10 text-purple-600 ring-1 ring-inset ring-purple-600",
                        "bg-purple-400/10 text-purple-400 ring-1 ring-inset ring-purple-400",
                        "bg-purple-500/10 text-purple-500 ring-1 ring-inset ring-purple-500",
                      ];

                      const color = colorClasses[index % colorClasses.length]; // cycle colors

                      return (
                        <span
                          key={index}
                          className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-black me-2 mb-2 ${color} uppercase`}
                        >
                          {badge.title}
                        </span>
                      );
                    })}
                  </span>
                ))}
              </div>
            </m.section>
          </div>
          <div className="scroll-container">
            <m.section
              ref={scrollRef}
              style={{ x: spring4 }}
              className="thumbnails-container"
            >
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-4">
                {content.map((item, index) => (
                  <span key={index}>
                    {item.list4.map((badge, index) => {
                      const colorClasses = [
                        "bg-purple-600/10 text-purple-600 ring-1 ring-inset ring-purple-600",
                        "bg-purple-400/10 text-purple-400 ring-1 ring-inset ring-purple-400",
                        "bg-purple-500/10 text-purple-500 ring-1 ring-inset ring-purple-500",
                        "bg-purple-200/10 text-purple-200 ring-1 ring-inset ring-purple-200",
                        "bg-blue-300/10 text-blue-300 ring-1 ring-inset ring-blue-300",
                        "bg-purple-300/10 text-purple-300 ring-1 ring-inset ring-purple-300",
                      ];

                      const color = colorClasses[index % colorClasses.length]; // cycle colors

                      return (
                        <span
                          key={index}
                          className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-black me-2 mb-2 ${color} uppercase`}
                        >
                          {badge.title}
                        </span>
                      );
                    })}
                  </span>
                ))}
              </div>
            </m.section>
          </div>
          <div className="scroll-container">
            <m.section
              ref={scrollRef}
              style={{ x: spring5 }}
              className="thumbnails-container"
            >
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-4">
                {content.map((item, index) => (
                  <span key={index}>
                    {item.list5.map((badge, index) => {
                      const colorClasses = [
                        "bg-purple-200/10 text-purple-200 ring-1 ring-inset ring-purple-200",
                        "bg-blue-300/10 text-blue-300 ring-1 ring-inset ring-blue-300",
                        "bg-purple-300/10 text-purple-300 ring-1 ring-inset ring-purple-300",
                        "bg-purple-600/10 text-purple-600 ring-1 ring-inset ring-purple-600",
                        "bg-purple-400/10 text-purple-400 ring-1 ring-inset ring-purple-400",
                        "bg-purple-500/10 text-purple-500 ring-1 ring-inset ring-purple-500",
                      ];

                      const color = colorClasses[index % colorClasses.length]; // cycle colors

                      return (
                        <span
                          key={index}
                          className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-black me-2 mb-2 ${color} uppercase`}
                        >
                          {badge.title}
                        </span>
                      );
                    })}
                  </span>
                ))}
              </div>
            </m.section>
          </div>
        </div>
      </LazyMotion>
      <div ref={ghostRef} style={{ height: scrollRange }} className="ghost" />
    </React.Fragment>
  );
};

export default ListVerticalScroll;
